import { createPortal } from 'react-dom';
import { intersectionWith, get } from 'lodash';
import React, { useMemo, useContext, useRef } from 'react';
import createPersistedState from '~/react-hooks/use-persisted-state';

import App from './App';
import Context from './Context';
import { getAssigneeId } from '~/utils/Filters';
import { Greet, UserSimple } from '~/types/models';
import useChecklist from '~/react-hooks/useChecklist';
import usePagination, { PaginationOptions } from '~/react-hooks/usePagination';
import WorkspaceContext from '~/App/Contexts/WorkspaceContext';
import useMeasureContentMain from '~/react-hooks/use-measure-content-main';
import useLanguage from '~/i18n';
import { GreetRecorderDrawer } from './Components/GreetRecorderDrawer';

function getPaginator<E>(url: string, options: PaginationOptions) {
  const [data, paginator, metadata] = usePagination<E>(url, options);

  return {
    data,
    metadata,
    paginator,
  };
}

const useAssigneeState = createPersistedState('tasklist-filter-assignee');

interface TasklistSortOrder {
  [userId: string]: string | undefined;
}

const useSortOrderState = createPersistedState('tasklist-sort-order-for-user');

function TodoList() {
  const target = useRef(document.getElementById('react-target'));
  const userId = window.Bonjoro.user.id;
  useLanguage('en');
  const { selectedWorkspace } = useContext(WorkspaceContext);

  const [assignee = {}, setAssignee] =
    useAssigneeState<Record<string, UserSimple>>();
  const [sortOrder, setSortOrder] = useSortOrderState<TasklistSortOrder | null>(
    null
  );
  const bulkSelect = useChecklist();
  let selectedSortOrder: string | undefined = get(sortOrder, userId);

  if (typeof selectedSortOrder === 'undefined') {
    selectedSortOrder = '-updated_at';
    setSortOrder({ ...sortOrder, [userId]: '-updated_at' });
  }

  const paginatorOptions = {
    requestParams: {
      limit: 25,
      simple: 1,
    },
  };

  const greets = getPaginator<Greet>('/api/v2/greets/todo', {
    key: 'todos',
    ...paginatorOptions,
    requestParams: {
      ...paginatorOptions.requestParams,
      sort: selectedSortOrder,
      assignee_id: getAssigneeId(userId, assignee),
      campaign_id: get(selectedWorkspace, 'id'),
    },
  });

  const users = getPaginator<UserSimple>('/api/v2/users', {
    key: 'users',
    ...paginatorOptions,
    itemUrl: '/api/v2/users/:id',
  });

  const greetIds = useMemo(
    () => greets.data.map((greet) => greet.id),
    [greets.data]
  );
  const selectedGreets = useMemo(() => {
    return intersectionWith(
      greets.data,
      bulkSelect.data.checked,
      (greet, greetId) => greetId === greet.id
    );
  }, [bulkSelect.data.count, greets.data]);

  const showFallback = useMemo(
    () => greets.data.length === 0 && greets.metadata.loading,
    [greets.metadata.loading]
  );

  const container = useMeasureContentMain();

  const context = useMemo(
    () => ({
      greets,
      greetIds,

      users,

      assignee,
      setAssignee,

      sortOrder,
      setSortOrder,

      bulkSelect,
      selectedGreets,

      showFallback,

      workspace: selectedWorkspace,
      container,
    }),
    [
      greets,
      greetIds,
      users,
      assignee,
      setAssignee,
      sortOrder,
      setSortOrder,
      bulkSelect,
      selectedGreets,
      showFallback,
      selectedWorkspace,
      container,
    ]
  );

  if (!target.current) {
    return null;
  }

  return createPortal(
    <Context.Provider value={context}>
      <App />
      <GreetRecorderDrawer />
    </Context.Provider>,
    target.current
  );
}

export default TodoList;
